import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { SubsectionAccordionItemComponent } from 'src/components/Reusable/Accordions/ShowPage/Subsections/SubsectionAccordion.components'
import { AuditRunTitle } from 'src/pages/AuditBot/AuditBot.components'
import {
  ControlsAbsoluteRoutes,
  ServicesAbsoluteRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'
import {
  ControlCheckDescriptionRow,
  ControlCheckMarkup,
  ControlCheckResultContainer,
  ControlCheckSourcesContainer,
  ControlCheckTestAccordionBody,
  ControlCheckTestAccordionHeader,
  ControlCheckTestAccordionItem,
  ControlCheckTestProcedureBody,
  ControlCheckTestProcedureContainer,
  ControlCheckTestProcedureDescription,
  ControlCheckTestProcedureIcon,
  ControlCheckTestProcedureNumber,
  TestProcedureEvidenceSourcesContainer,
} from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.styles'
import {
  CONTROL_TEST_NAMES,
  PASSED_CONTROL_CHECK_TEXT,
} from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { ControlCheckProvider } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/context/AuditBotControlCheckContext'
import {
  DocumentsReadyCheckContainer,
  DocumentsReadyCheckHeader,
} from 'src/pages/AuditBot/accordion/subsection/AuditBotSubSectionAccordion.styles'
import { ControlCheckSourceResultMismatch } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.components'
import { DropdownSVG } from 'src/components/Icons/Basic'
import { SectionTitle } from 'src/components/Reusable/Text/Text.styles'
import {
  DocumentSourceRelevantDate,
  SourceContainer,
  SourceDeleted,
  SourceSummary,
} from 'src/components/Reusable/Sources/sources.styles'
import { SOURCE_RESULT } from '@trustero/trustero-api-web/lib/source/source_pb'
import { useEvidenceGroupIfExists } from 'src/pages/Evidence/evidence.hooks'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { formatTimestamp } from 'src/Utils/formatDate'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { CitationLink } from 'src/pages/SecurityQuestionnaire/show/securityQuestionnaireAccordion.components'
import {
  Artifact,
  OeControlTestCitation,
  OeControlTestCitationType,
  OeControlTestDetermination,
  OeControlTestResult,
} from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { openInNewTab } from 'src/Utils/globalHelpers'
import { getEvidenceLink } from 'src/pages/Evidence/evidence.helpers'
import { ModalFormId } from 'src/components/ModalForms'
import { useModalState } from 'src/Modal/ModalStateContext'
import { cleanTestProcedureDescription } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.helpers'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import {
  TOOLTIP_TRIGGERS,
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { TestTableSourceType } from '@trustero/trustero-api-web/lib/auditbotapi/auditbotapi_pb'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import { useArtifact } from '../../conversation/chatConversation.hooks'
import { TestTableArtifactModal } from '../../modals/TestTableArtifactModal'
import { CrossReferencedArtifactModal } from '../../modals/CrossReferencedArtifactModal'
import { useArtifactModalContext } from '../ArtifactModalContext'
import { AuditScanControlTestAccordion } from './AuditScanArtifact.styles'
import { AuditScanResultType } from './AuditScanArtifact.types'
import {
  getAuditScanResultIcon,
  getOESourceResultIcon,
  getSourceResultFromDetermination,
  getSourceResultFromExceptionsFound,
} from './AuditScanArtifact.helpers'
import { AuditScanArtifactJoinInfo } from './AuditScanArtifact.constants'

export const ControlCrossReferencedDataSourceTitle = ({
  artifact,
  sourceId,
  joinInfo,
}: {
  artifact: Artifact
  sourceId: string
  joinInfo?: AuditScanArtifactJoinInfo
}): JSX.Element => {
  const { openModal } = useModalState()
  const crossReferencedData = artifact.getCrossReferencedDataArtifact()
  const caption = crossReferencedData?.getJoinDescription()
  let truncatedCaption =
    caption && caption?.length > 50 ? caption.slice(0, 50) + '...' : caption
  if (joinInfo && joinInfo === AuditScanArtifactJoinInfo.LEFT) {
    truncatedCaption = `${truncatedCaption} (Left)`
  } else if (joinInfo && joinInfo === AuditScanArtifactJoinInfo.RIGHT) {
    truncatedCaption = `${truncatedCaption} (Right)`
  }
  const { selectedArtifact, setSelectedArtifact } = useArtifactModalContext()
  const isSelected = sourceId === selectedArtifact
  if (!crossReferencedData) {
    return <></>
  }

  return (
    <>
      <FlexRow gap={8} justify={FlexAlign.FLEX_START}>
        <span>{truncatedCaption}</span>
        <CitationLink
          citationType={{
            onClick: () => {
              setSelectedArtifact(sourceId)
              openModal(ModalFormId.CROSS_REFERENCED_ARTIFACT_MODAL)
            },
            pageNumbers: [],
          }}
        />
      </FlexRow>
      {isSelected && (
        <CrossReferencedArtifactModal
          crossReferencedData={crossReferencedData}
        />
      )}
    </>
  )
}

export const ControlTestTableSourceTitle = ({
  artifact,
  showPassFail,
  sourceId,
}: {
  artifact: Artifact
  showPassFail?: boolean
  sourceId: string
}): JSX.Element => {
  const testTableArtifact = artifact.getTestTableArtifact()
  const testTable = testTableArtifact?.getTestTable()
  const resultIcon = getOESourceResultIcon(
    testTable?.getPassed() ? SOURCE_RESULT.PASS : SOURCE_RESULT.FAIL,
  )

  const { openModal } = useModalState()
  const { selectedArtifact, setSelectedArtifact } = useArtifactModalContext()
  const isSelected = sourceId === selectedArtifact
  const caption = testTable?.getCaption()
  const truncatedCaption =
    caption && caption?.length > 50 ? caption.slice(0, 50) + '...' : caption
  if (!testTable) {
    return <></>
  }

  return (
    <>
      <FlexRow gap={8} justify={FlexAlign.FLEX_START}>
        {showPassFail && resultIcon}
        <span>{truncatedCaption}</span>
        <CitationLink
          citationType={{
            onClick: () => {
              setSelectedArtifact(sourceId)
              openModal(ModalFormId.TEST_TABLE_ARTIFACT_MODAL)
            },
            pageNumbers: [],
          }}
        />
      </FlexRow>
      {isSelected && <TestTableArtifactModal testTable={testTable} />}
    </>
  )
}

export const ControlCheckArtifactSourceTitle = ({
  sourceId,
  showPassFail,
}: {
  sourceId: string
  showPassFail?: boolean
}): JSX.Element | null => {
  const { data, error } = useArtifact(sourceId)
  const artifactData = data?.getArtifact()
  const testTable = artifactData?.getTestTableArtifact()?.getTestTable()
  const crossReferencedData = artifactData?.getCrossReferencedDataArtifact()

  if (error || !artifactData) {
    return (
      <FlexRow gap={8} justify={FlexAlign.FLEX_START}>
        <SourceDeleted>(Source not available)</SourceDeleted>
      </FlexRow>
    )
  }

  return testTable ? (
    <ControlTestTableSourceTitle
      artifact={artifactData}
      showPassFail={showPassFail}
      sourceId={sourceId}
    />
  ) : crossReferencedData ? (
    <ControlCrossReferencedDataSourceTitle
      artifact={artifactData}
      sourceId={sourceId}
    />
  ) : null
}
export const ControlCheckContextSourceTitle = (): JSX.Element | null => {
  const { pageContext } = useParams()

  return (
    <FlexRow gap={8} justify={FlexAlign.FLEX_START}>
      <span>Organizational Context</span>
      <CitationLink
        citationType={{
          onClick: () => {
            openInNewTab(`/${pageContext}/${ServicesAbsoluteRoutes.INDEX}`)
          },
          pageNumbers: [],
        }}
      />
    </FlexRow>
  )
}

export const ControlCheckEvidenceSourceTitle = ({
  sourceId,
  result,
  showPassFail,
  joinInfo,
  caption,
}: {
  sourceId: string
  result?: SOURCE_RESULT
  showPassFail?: boolean
  joinInfo?: AuditScanArtifactJoinInfo
  caption?: string
}): JSX.Element => {
  const { data } = useEvidenceGroupIfExists(sourceId)
  const isDeleted = !data?.getExists()
  const { pageContext } = useParams()
  const resultIcon = result !== undefined ? getOESourceResultIcon(result) : null
  if (isDeleted) {
    return (
      <FlexRow gap={8} justify={FlexAlign.FLEX_START}>
        {showPassFail && resultIcon}
        {caption && <span>{caption}</span>}
        <SourceDeleted>(Deleted)</SourceDeleted>
      </FlexRow>
    )
  }
  const evidence = data?.getEvidenceGroup()
  if (!evidence) {
    return <></>
  }
  const evidenceCaption = evidence.getCaption()
  const relevantDate = evidence.getRelevantDate()
  const joinInfoText =
    joinInfo === AuditScanArtifactJoinInfo.LEFT
      ? ' (Left)'
      : joinInfo === AuditScanArtifactJoinInfo.RIGHT
      ? ' (Right)'
      : ''
  const truncatedCaption =
    evidenceCaption && evidenceCaption?.length > 50 ? (
      <Tooltip
        id={evidenceCaption}
        placement={TooltipPositions.bottom}
        overlayType={TooltipOverlayType.popover}
        tooltipBody={`${evidenceCaption}${joinInfoText}`}
        trigger={[TOOLTIP_TRIGGERS.HOVER, TOOLTIP_TRIGGERS.FOCUS]}
      >
        <span style={{ cursor: 'default' }}>
          {evidenceCaption.slice(0, 50) + '...'} {joinInfoText}
        </span>
      </Tooltip>
    ) : (
      <span>{`${evidenceCaption}${joinInfoText}`}</span>
    )
  return (
    <FlexRow gap={8} justify={FlexAlign.FLEX_START}>
      {showPassFail && resultIcon}
      {truncatedCaption}
      <DocumentSourceRelevantDate>
        {relevantDate &&
          formatTimestamp(relevantDate, DATE_FORMATS.ISO_WITH_TIME)}
      </DocumentSourceRelevantDate>
      <CitationLink
        citationType={{
          onClick: () => {
            openInNewTab(getEvidenceLink(evidence.getId(), '', pageContext))
          },
          pageNumbers: [],
        }}
        mimeType={evidence.getMime()}
      />
    </FlexRow>
  )
}

export const ControlCheckSourceTitle = ({
  sourceId,
  sourceType,
  result,
  showPassFail,
  joinInfo,
  caption,
}: {
  sourceId: string
  sourceType: OeControlTestCitationType | TestTableSourceType
  result?: SOURCE_RESULT
  showPassFail?: boolean
  joinInfo?: AuditScanArtifactJoinInfo
  caption?: string
}): JSX.Element | null => {
  switch (sourceType) {
    case OeControlTestCitationType.EVIDENCE:
    case TestTableSourceType.EVIDENCE:
      return (
        <ControlCheckEvidenceSourceTitle
          sourceId={sourceId}
          result={result}
          showPassFail={showPassFail}
          joinInfo={joinInfo}
          caption={caption}
        />
      )
    case OeControlTestCitationType.ARTIFACT:
    case TestTableSourceType.ARTIFACT:
      return (
        <ControlCheckArtifactSourceTitle
          sourceId={sourceId}
          showPassFail={showPassFail}
        />
      )
    case OeControlTestCitationType.CONTEXT:
      return <ControlCheckContextSourceTitle />
    case OeControlTestCitationType.UNKNOWN:
    case TestTableSourceType.UNKNOWN:
    default:
      return null
  }
}

const ControlCheckSourceComponent = ({
  source,
  isOperatingEffectiveness,
  overallResult,
}: {
  source: OeControlTestCitation
  isOperatingEffectiveness?: boolean
  overallResult?: SOURCE_RESULT
}): JSX.Element => {
  const result = getSourceResultFromExceptionsFound(source.getExceptionsFound())
  const showMismatch = result === SOURCE_RESULT.FAIL && result !== overallResult
  const summary = source.getFindingSummary()
  const sourceId = source.getEvidenceId()
  const sourceType = source.getCitationType()
  const sourceCaption = source.getEvidenceCaption()
  return (
    <SourceContainer>
      <ControlCheckSourceTitle
        sourceId={sourceId}
        sourceType={sourceType}
        result={result}
        showPassFail={isOperatingEffectiveness}
        caption={sourceCaption}
      />
      <SourceSummary $isItalic>{summary}</SourceSummary>
      {showMismatch && <ControlCheckSourceResultMismatch />}
    </SourceContainer>
  )
}

const ControlCheckTestProcedure = ({
  orderNumber,
  testProcedure,
  total,
}: {
  orderNumber: number
  testProcedure: OeControlTestResult
  total: number
}): JSX.Element => {
  const description = cleanTestProcedureDescription(
    testProcedure.getTestString(),
  )
  const summary = testProcedure.getReasoning()
  const evidenceResults = testProcedure.getCitationsList()
  const showNumber = total > 1
  const overAllResult = getSourceResultFromDetermination(
    testProcedure.getDetermination(),
  )
  const tpIcon = getOESourceResultIcon(overAllResult)

  return (
    <ControlCheckTestProcedureContainer $isFullWidth>
      <ControlCheckTestProcedureIcon>{tpIcon}</ControlCheckTestProcedureIcon>
      {showNumber && (
        <ControlCheckTestProcedureNumber>
          {orderNumber}.
        </ControlCheckTestProcedureNumber>
      )}
      <ControlCheckTestProcedureDescription $showNumber={showNumber}>
        <ControlCheckMarkup markdown={description} />
      </ControlCheckTestProcedureDescription>
      <ControlCheckTestProcedureBody $showNumber={showNumber}>
        <ControlCheckMarkup
          markdown={summary}
          sectionProps={{ fontStyle: 'italic' }}
        />
        {evidenceResults.length > 0 && (
          <TestProcedureEvidenceSourcesContainer>
            Evidence and Sources
            {evidenceResults.map((evidenceResult, index) => (
              <ControlCheckSourceComponent
                key={index}
                source={evidenceResult}
                isOperatingEffectiveness
                overallResult={overAllResult}
              />
            ))}
          </TestProcedureEvidenceSourcesContainer>
        )}
      </ControlCheckTestProcedureBody>
    </ControlCheckTestProcedureContainer>
  )
}

export const OperatingEffectivenessArtifactSources = ({
  oeSources,
  isLoading,
}: {
  oeSources: OeControlTestResult[]
  isLoading?: boolean
}): JSX.Element | null =>
  isLoading ? (
    <TableListLoader />
  ) : oeSources.length > 0 ? (
    <ControlCheckSourcesContainer $isFullWidth>
      <SectionTitle $size={14} $mb={8}>
        TEST DETAILS
      </SectionTitle>
      {oeSources.map((testProcedure, index) => (
        <ControlCheckTestProcedure
          key={index}
          orderNumber={index + 1}
          testProcedure={testProcedure}
          total={oeSources.length}
        />
      ))}
    </ControlCheckSourcesContainer>
  ) : null

const AuditScanControlCheckResult = ({
  checks,
  controlId,
  expandedChecks,
  setExpandedChecks,
}: {
  checks: OeControlTestResult[]
  controlId: string
  expandedChecks: string[]
  setExpandedChecks?: React.Dispatch<React.SetStateAction<string[]>>
}): JSX.Element => {
  const isExpanded = useMemo(
    () => expandedChecks.includes(controlId),
    [expandedChecks, controlId],
  )
  const resultIcon = useMemo(() => {
    const result = checks.every(
      (check) => check.getDetermination() === OeControlTestDetermination.PASS,
    )
      ? OeControlTestDetermination.PASS
      : OeControlTestDetermination.FAIL
    return getAuditScanResultIcon(result, AuditScanResultType.INDIVIDUAL)
  }, [checks])
  const testName = CONTROL_TEST_NAMES.SPOT_CHECK
  const testNameText = `${testName} (Advanced)`

  const handleControlCheckClick = (
    e: React.MouseEvent,
    eventKey: string,
    keys: string[],
    setActiveKeys: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (keys.includes(eventKey)) {
      keys = keys.filter((key) => key !== eventKey)
      return setActiveKeys(keys)
    }
    return setActiveKeys([...keys, eventKey])
  }

  return (
    <DocumentsReadyCheckContainer>
      <ControlCheckTestAccordionItem eventKey={controlId}>
        <ControlCheckTestAccordionHeader
          $isActive={isExpanded}
          onClick={(e: React.MouseEvent) =>
            setExpandedChecks &&
            handleControlCheckClick(
              e,
              controlId,
              expandedChecks,
              setExpandedChecks,
            )
          }
        >
          <DocumentsReadyCheckHeader>
            {resultIcon}
            {testNameText}
            <DropdownSVG className="control-check-chevron" />
          </DocumentsReadyCheckHeader>
        </ControlCheckTestAccordionHeader>
        <ControlCheckTestAccordionBody>
          <ControlCheckResultContainer>
            <ControlCheckDescriptionRow>
              {PASSED_CONTROL_CHECK_TEXT[`${testName}` as CONTROL_TEST_NAMES]}
            </ControlCheckDescriptionRow>
            <OperatingEffectivenessArtifactSources oeSources={checks} />
          </ControlCheckResultContainer>
        </ControlCheckTestAccordionBody>
      </ControlCheckTestAccordionItem>
    </DocumentsReadyCheckContainer>
  )
}

export const AuditScanControlCheckResults = ({
  checks,
  controlId,
  expandedChecks,
  setExpandedChecks,
}: {
  checks: OeControlTestResult[]
  controlId: string
  expandedChecks: string[]
  setExpandedChecks?: React.Dispatch<React.SetStateAction<string[]>>
}): JSX.Element => (
  <ControlCheckProvider>
    <AuditScanControlCheckResult
      key={`${controlId}`}
      checks={checks}
      controlId={controlId}
      expandedChecks={expandedChecks}
      setExpandedChecks={setExpandedChecks}
    />
  </ControlCheckProvider>
)

const AuditScanControlAccordionItem = ({
  controlId,
  modelId,
  controlName,
  controlChecks,
  activeKeys,
  setActiveKeys,
}: {
  controlId: string
  modelId: string
  controlName: string
  controlChecks: OeControlTestResult[]
  activeKeys: string[]
  setActiveKeys: React.Dispatch<React.SetStateAction<string[]>>
}): JSX.Element => {
  const [expandedChecks, setExpandedChecks] = useState<string[]>([])
  const controlResultIcon = useMemo(() => {
    const result = controlChecks.every(
      (check) => check.getDetermination() === OeControlTestDetermination.PASS,
    )
      ? OeControlTestDetermination.PASS
      : OeControlTestDetermination.FAIL
    return getAuditScanResultIcon(result, AuditScanResultType.OVERALL)
  }, [controlChecks])

  const handleControlClick = (
    e: React.MouseEvent,
    eventKey: string,
    keys: string[],
    setActiveKeys: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (keys.includes(eventKey)) {
      keys = keys.filter((key) => key !== eventKey)
      return setActiveKeys(keys)
    }
    return setActiveKeys([...keys, eventKey])
  }

  return (
    <SubsectionAccordionItemComponent
      key={controlId}
      eventKey={controlId}
      icon={controlResultIcon}
      title={
        <AuditRunTitle
          modelId={modelId}
          text={controlName}
          route={ControlsAbsoluteRoutes.SHOW}
        />
      }
      notes=""
      body={
        <AuditScanControlTestAccordion activeKey={expandedChecks}>
          <AuditScanControlCheckResults
            checks={controlChecks}
            controlId={controlId}
            expandedChecks={expandedChecks}
            setExpandedChecks={setExpandedChecks}
          />
        </AuditScanControlTestAccordion>
      }
      handleHeaderClick={(e: React.MouseEvent) =>
        handleControlClick(e, controlId, activeKeys, setActiveKeys)
      }
      handleBodyClick={(e: React.MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()
      }}
      $customGridLayout="min-content 1fr"
    />
  )
}

export const AuditScanArtifactContentRenderer = ({
  control,
  controlChecks,
}: {
  control: Control
  controlChecks: OeControlTestResult[]
}): JSX.Element => {
  const [activeKeys, setActiveKeys] = useState<string[]>([control.getId()])

  return (
    <AuditScanControlAccordionItem
      key={control.getId()}
      controlId={control.getId()}
      modelId={control.getModelId()}
      controlName={`${control.getModelId()} ${control.getName()}`}
      controlChecks={controlChecks}
      activeKeys={activeKeys}
      setActiveKeys={setActiveKeys}
    />
  )
}
